import { useMemo } from 'react';
import { CancellationWaitlistNotifyMode } from '@prisma/client';
import isEqual from 'lodash.isequal';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import FormSmallModal, { InputRow } from '../global/FormSmallModal';
import useUser from '../../hooks/global/useUser';
import { isUserAdminOrManager } from '../../utils/global/helper';
import useUpdateCancellationWaitlistSettings from '../../hooks/cancellation-waitlist/useUpdateCancellationWaitlistSettings';
import useCancellationWaitlistSettings from '../../hooks/cancellation-waitlist/useCancellationWaitlistSettings';
import { CancellationWaitlistSettings } from '../../types/cancellation-waitlist';

interface Props {
  open: boolean;
  onClose: () => void;
}

const waitlistSettingsSchema = yup.object({
  cancellationWaitlistEnabled: yup.boolean().required(),
  cancellationWaitlistNotifyMode: yup
    .mixed<CancellationWaitlistNotifyMode>()
    .oneOf(Object.values(CancellationWaitlistNotifyMode))
    .required(),
});

function CancellationWaitlistSettingsFormModal({ open, onClose }: Props) {
  const { user } = useUser();
  const { data: settings } = useCancellationWaitlistSettings();

  const { mutateAsync: updateCancellationWaitlistSettings } =
    useUpdateCancellationWaitlistSettings({
      onSuccess: () => {
        toast.success('Waitlist settings updated!');
      },
      onError: (message: string) => {
        toast.error(message);
      },
    });

  const initialValues: CancellationWaitlistSettings = useMemo(
    () => ({
      cancellationWaitlistEnabled:
        settings?.cancellationWaitlistEnabled ?? true,
      cancellationWaitlistNotifyMode:
        settings?.cancellationWaitlistNotifyMode ??
        CancellationWaitlistNotifyMode.INSTANT_ALL,
    }),
    [
      settings?.cancellationWaitlistEnabled,
      settings?.cancellationWaitlistNotifyMode,
    ],
  );

  const showStudioManagerAlert =
    !!user && user.business.type === 'STUDIO' && isUserAdminOrManager(user);

  const inputRows: InputRow<CancellationWaitlistSettings>[] = [
    [
      {
        inputType: 'switch',
        label: 'Cancellation waitlist',
        description: 'Notify waitlisted clients when a booked slot opens up.',
        name: 'cancellationWaitlistEnabled',
      },
    ],
    [
      {
        inputType: 'radio-select',
        label: 'Notification style',
        isLabelMultiline: true,
        name: 'cancellationWaitlistNotifyMode',
        options: [
          {
            label:
              'Notify at once — everyone on the waitlist is texted at the same time. First to book wins.',
            value: CancellationWaitlistNotifyMode.INSTANT_ALL,
          },
          {
            label:
              'Stagger notifications — we text people a few at a time, and stop as soon as the slot is booked.',
            value: CancellationWaitlistNotifyMode.STAGGERED,
          },
        ],
      },
    ],
  ];

  return (
    <FormSmallModal
      open={open}
      onClose={onClose}
      allowUntouched={false}
      alert={
        showStudioManagerAlert
          ? {
              type: 'info',
              dismissible: false,
              animate: false,
              title: "You're only editing your own waitlist settings.",
              subtitle:
                'Individual employees manage their own waitlist settings from the mobile app. To update the waitlist settings for all artists, go to Booking Settings → Default Artist Settings.',
            }
          : undefined
      }
      form={{
        title: 'Waitlist Settings',
        initialValues,
        inputRows,
        onSubmit: async values => {
          const isChanged = !isEqual(values, initialValues);
          if (!isChanged) {
            onClose();
            return;
          }
          await updateCancellationWaitlistSettings(values);
          onClose();
        },
        validationSchema: waitlistSettingsSchema,
        submitButtonText: 'Save',
      }}
      maxWidth={500}
    />
  );
}

export default CancellationWaitlistSettingsFormModal;
